import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Waves, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";

function Metric({ label, value, sub, tone }) {
  const tones = {
    good: "border-emerald-200 bg-emerald-50",
    bad: "border-red-200 bg-red-50",
    warn: "border-amber-200 bg-amber-50",
    neutral: "border-slate-200 bg-white",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone || "neutral"]}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-semibold font-mono text-slate-900 mt-0.5">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function CashFlowTab({ appId, app, onRefresh, busyState }) {
  const [busy, setBusy] = busyState;
  const cf = app.cashflow_analysis;

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/applications/${appId}/cashflow/run`);
      toast.success("Cash-flow analysis complete");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cash-flow analysis failed");
    } finally { setBusy(false); }
  };

  const pct = (x) => (x || x === 0 ? `${Math.round(x * 1000) / 10}%` : "—");

  return (
    <Card className="p-5 border-slate-200" data-testid="cashflow-tab">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Waves size={18} /> Cash-Flow Underwriting</div>
          <div className="text-sm text-slate-600">Bank-statement based repayment-ability analysis (salaried & self-employed).</div>
        </div>
        <Button onClick={run} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="run-cashflow-btn">
          {cf ? "Re-run analysis" : "Run cash-flow analysis"}
        </Button>
      </div>

      {!cf ? (
        <div className="text-sm text-slate-500" data-testid="cashflow-empty">
          No analysis yet. Running it will use the Account Aggregator bank statement (auto-fetched if not pulled already) to compute verified income, surplus, FOIR and stability scores.
        </div>
      ) : (
        <div className="space-y-4" data-testid="cashflow-result">
          <div className="text-xs text-slate-500">
            {cf.bank} · A/c {cf.account_masked} · {cf.period_months} months · computed {String(cf.computed_at).slice(0, 10)}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Verified monthly income" value={formatINR(cf.verified_monthly_income)} tone="good" />
            <Metric label="Estimated monthly income" value={formatINR(cf.estimated_monthly_income)} />
            <Metric label="Declared income" value={formatINR(cf.declared_monthly_income)} />
            <Metric label="Income confidence" value={`${cf.income_confidence_score}/100`} tone={cf.income_confidence_score >= 70 ? "good" : cf.income_confidence_score >= 50 ? "warn" : "bad"} />
            <Metric label="Avg monthly credits" value={formatINR(cf.avg_monthly_credits)} sub={<span className="inline-flex items-center gap-1"><TrendingUp size={11} /> inflow</span>} />
            <Metric label="Avg monthly debits" value={formatINR(cf.avg_monthly_debits)} sub={<span className="inline-flex items-center gap-1"><TrendingDown size={11} /> outflow</span>} />
            <Metric label="Monthly surplus" value={formatINR(cf.monthly_surplus)} tone={cf.monthly_surplus > 0 ? "good" : "bad"} />
            <Metric label="Avg balance" value={formatINR(cf.avg_monthly_balance)} />
            <Metric label="Income consistency" value={pct(cf.income_consistency_pct / 100)} />
            <Metric label="Volatility" value={pct(cf.cashflow_volatility_pct / 100)} tone={cf.cashflow_volatility_pct > 50 ? "warn" : "neutral"} />
            <Metric label="EMI / cheque bounces" value={cf.emi_bounce_count} tone={cf.emi_bounce_count > 0 ? "bad" : "good"} />
            <Metric label="Existing EMI" value={formatINR(cf.existing_emi_obligations)} />
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Underwriting output</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Cash-flow stability score" value={`${cf.cashflow_stability_score}/100`} tone={cf.cashflow_stability_score >= 65 ? "good" : "warn"} />
              <Metric label="Bank statement risk" value={`${cf.bank_statement_risk_score}/100`} tone={cf.bank_statement_risk_score <= 35 ? "good" : "bad"} />
              <Metric label="Max EMI capacity" value={formatINR(cf.max_emi_capacity)} tone="good" />
              <Metric label="Recommended loan" value={formatINR(cf.recommended_loan_amount)} tone="good" />
              <Metric label="Projected FOIR" value={pct(cf.foir_projected)} tone={cf.foir_projected > 0.55 ? "bad" : "neutral"} />
              <Metric label="PTI" value={pct(cf.pti)} />
              <Metric label="Recommended down payment" value={cf.recommended_down_payment != null ? formatINR(cf.recommended_down_payment) : "—"} />
              <Metric label="Recommended LTV" value={cf.recommended_ltv != null ? pct(cf.recommended_ltv) : "—"} />
            </div>
          </div>

          {(cf.reason_codes || []).length > 0 && (
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="cashflow-reasons">
              <div className="font-semibold mb-1 flex items-center gap-1"><AlertTriangle size={14} /> Reason codes</div>
              <ul className="list-disc pl-5 space-y-1">{cf.reason_codes.map((r) => <li key={r}>{r}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
